import React, { useEffect, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import Dropdown from "../DropDown";
import { fetchWithCache } from "../../CacheDB/cached-Ref-Api";

interface AssetTypeData {
  generation_type_code: string;
  disabled: boolean;
}

interface AssetTypeDropdownProps {
  selectedAssetType: string;
  onAssetTypeChange: (selectedAssetType: string) => void;
  disabled: boolean;
  className?: string;
  onFocus?: () => void;
}

const AssetTypeDropdown: React.FC<AssetTypeDropdownProps> = ({
  selectedAssetType,
  onAssetTypeChange,
  className = "",
  disabled,
  onFocus,
}) => {
  const [assetTypeData, setAssetTypeData] = useState<AssetTypeData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAssetTypes = async () => {
      const startTime = performance.now();
      try {
        const data = await fetchWithCache(
          "generation-subtype",
          "/ref-data/generation-subtype"
        ) as AssetTypeData[];
        

        const uniqueData = Array.from(
          new Map(data.map((item: AssetTypeData) => [
            item.generation_type_code,
            item,
          ])).values()
        );

        setAssetTypeData(uniqueData);

        datadogRum.addAction("Fetched Asset Types (Cached/Live)", {
          assetTypeCount: uniqueData.length,
        });
      } catch (err) {
        setError("Failed to load Asset Types");
        datadogRum.addError("Asset Type Fetch Failed", { error: err });
      } finally {
        setLoading(false);
        const endTime = performance.now();
        datadogRum.addAction("Asset Type API Duration", {
          duration: endTime - startTime,
        });
      }
    };

    fetchAssetTypes();
  }, []);

  const options = assetTypeData.map((type) => ({
    value: type.generation_type_code,
    label: type.generation_type_code,
    disabled: type.disabled,
  }));

  return (
    <Dropdown
      options={options}
      selectedValue={selectedAssetType}
      onValueChange={onAssetTypeChange}
      disabled={disabled || loading || !!error}
      className={className}
      placeholder={loading ? "Loading Asset Types..." : error ?? "Select Asset Type"}
      onFocus={onFocus}
    />
  );
};

export default AssetTypeDropdown;
